/**
 * Phase 15 — Reconciliar diferencias.
 *
 * Renders inside InventoryDetailPage when the process is CLOSED and the
 * stats show at least one wrong-location row (status=2) or one pending
 * row (status=0/4). Lets a permitted operator apply one of two
 * adjustment actions per row:
 *
 *  - ACCEPT_LOCATION → asset.locationId becomes the process's location;
 *    InventoryProcessAsset.AssetStatus flips from 2 to 1 (OK).
 *  - MARK_MISSING → asset.isActive=false; AssetStatus flips 0→3
 *    (treated like an incident in the stats card).
 *
 * Every adjustment writes one InventoryAdjustment audit row server-side.
 * Forbidden if the caller lacks (inventory, adjust) — the mutation
 * returns 403 and we surface a tooltip on the button.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Wrench, MapPin, AlertTriangle, History, Sparkles, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  useInventoryAssetsPaginated,
  useInventoryAdjustments,
  useAdjustInventoryAsset,
  InventoryAdjustmentAction,
} from '@/api/hooks/useInventory';
import { useReconcileSuggestions, type AiReconcileSuggestion } from '@/api/hooks/useAiChat';
import { useFeature } from '@/contexts/FeatureFlagContext';
import { toast } from 'sonner';

interface Props {
  processId: number;
  isClosed: boolean;
  locationName: string | null;
}

export default function InventoryReconcileCard({ processId, isClosed, locationName }: Props) {
  const { t } = useTranslation(['inventory', 'common']);

  // Pull the two diff buckets straight from the existing paginated endpoint —
  // small page sizes because the panel only shows the first batch with a
  // "Ver más" hint if there are extras. Keeps it cheap on big inventories.
  const wrongLocation = useInventoryAssetsPaginated(
    processId,
    { page: 1, pageSize: 25, statusFilter: [2] },
    isClosed,
  );
  const pending = useInventoryAssetsPaginated(
    processId,
    { page: 1, pageSize: 25, statusFilter: [0, 4] },
    isClosed,
  );
  const history = useInventoryAdjustments(processId, isClosed);
  const adjust = useAdjustInventoryAsset(processId);
  const suggest = useReconcileSuggestions();
  // The suggestions endpoint only exists where the AI module is on; without
  // this the button showed on every tenant and silently did nothing on 404.
  const aiEnabled = useFeature('ai');
  const suggestions = useMemo(() => {
    const map = new Map<string, AiReconcileSuggestion>();
    (suggest.data ?? []).forEach((s) => map.set(s.assetId, s));
    return map;
  }, [suggest.data]);

  const [busyAssetId, setBusyAssetId] = useState<string | null>(null);

  // Until the operator presses Finalizar the diffs aren't stable, so
  // hide the panel entirely. The empty-state branch below renders only
  // when isClosed=true and both buckets came back empty.
  if (!isClosed) return null;

  const wrongCount = wrongLocation.data?.totalCount ?? 0;
  const pendingCount = pending.data?.totalCount ?? 0;
  const adjustmentCount = history.data?.length ?? 0;

  // Nothing to reconcile AND nothing in the history → skip rendering.
  // The operator finalized a clean count; no need to clutter the page.
  if (wrongCount === 0 && pendingCount === 0 && adjustmentCount === 0) return null;

  const handleAdjust = async (assetId: string, action: InventoryAdjustmentAction) => {
    try {
      setBusyAssetId(assetId);
      await adjust.mutateAsync({ assetId, action });
      toast.success(
        action === 'ACCEPT_LOCATION'
          ? t('reconcile.acceptSuccess', { defaultValue: 'Ubicación aceptada.' })
          : t('reconcile.missingSuccess', { defaultValue: 'Marcado como faltante.' }),
      );
    } catch (err) {
      const msg = (err as { response?: { status?: number; data?: { message?: string } } })
        ?.response?.data?.message;
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        toast.error(
          t('reconcile.forbidden', {
            defaultValue: 'Necesitas el permiso "inventory.adjust" para aplicar ajustes.',
          }),
        );
      } else {
        toast.error(msg ?? t('common:errors.generic', { defaultValue: 'Error al aplicar el ajuste.' }));
      }
    } finally {
      setBusyAssetId(null);
    }
  };

  const isBusy = (assetId: string) => busyAssetId === assetId && adjust.isPending;

  const renderSuggestion = (assetId: string) => {
    const s = suggestions.get(assetId);
    if (!s) return null;
    const pct = Math.round(s.confidence * 100);
    const label =
      s.action === 'ACCEPT_LOCATION'
        ? t('reconcile.aiAccept', { defaultValue: 'Aceptar' })
        : s.action === 'MARK_MISSING'
          ? t('reconcile.aiMissing', { defaultValue: 'Faltante' })
          : t('reconcile.aiReview', { defaultValue: 'Revisar' });
    const tone =
      s.action === 'ACCEPT_LOCATION'
        ? 'border-green-600/40 text-green-600'
        : s.action === 'MARK_MISSING'
          ? 'border-red-600/40 text-red-600'
          : 'text-muted-foreground';
    return (
      <Badge variant="outline" className={`gap-1 ${tone}`} title={s.rationale}>
        <Sparkles className="h-3 w-3" />
        {label} · {pct}%
      </Badge>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              {t('reconcile.title', { defaultValue: 'Reconciliar diferencias' })}
            </CardTitle>
            <CardDescription>
              {t('reconcile.description', {
                defaultValue:
                  'Aplica una decisión por activo. Cada ajuste queda registrado con tu usuario y la fecha.',
              })}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {aiEnabled && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => suggest.mutate(processId)}
                disabled={suggest.isPending || (wrongCount === 0 && pendingCount === 0)}
              >
                {suggest.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="mr-2 h-4 w-4" />
                )}
                {t('reconcile.aiSuggest', { defaultValue: 'Sugerencias IA' })}
              </Button>
            )}
            {adjustmentCount > 0 && (
              <Badge variant="secondary" className="gap-1">
                <History className="h-3 w-3" />
                {adjustmentCount} {t('reconcile.applied', { defaultValue: 'aplicados' })}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* WRONG-LOCATION BUCKET ------------------------------------------------- */}
        <section>
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="h-4 w-4 text-yellow-600" />
            <h4 className="font-semibold text-sm">
              {t('reconcile.wrongLocationHeading', {
                defaultValue: 'Ubicación incorrecta',
              })}
            </h4>
            <Badge variant="outline">{wrongCount}</Badge>
          </div>
          {wrongLocation.isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : wrongCount === 0 ? (
            <p className="text-xs text-muted-foreground">
              {t('reconcile.noWrongLocation', {
                defaultValue: 'No hay activos en ubicación incorrecta.',
              })}
            </p>
          ) : (
            <ul className="divide-y rounded-md border">
              {wrongLocation.data!.items.map((a) => (
                <li
                  key={a.assetId}
                  className="flex items-center justify-between gap-4 px-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{a.assetId}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {a.assetName || '—'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {renderSuggestion(a.assetId)}
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={isBusy(a.assetId)}
                      onClick={() => handleAdjust(a.assetId, 'ACCEPT_LOCATION')}
                    >
                      {t('reconcile.acceptButton', {
                        defaultValue: 'Aceptar nueva ubicación',
                      })}
                      {locationName ? ` → ${locationName}` : ''}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* PENDING / MISSING BUCKET --------------------------------------------- */}
        <section>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <h4 className="font-semibold text-sm">
              {t('reconcile.pendingHeading', { defaultValue: 'No escaneados' })}
            </h4>
            <Badge variant="outline">{pendingCount}</Badge>
          </div>
          {pending.isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : pendingCount === 0 ? (
            <p className="text-xs text-muted-foreground">
              {t('reconcile.noPending', {
                defaultValue: 'Todos los activos fueron escaneados.',
              })}
            </p>
          ) : (
            <ul className="divide-y rounded-md border">
              {pending.data!.items.map((a) => (
                <li
                  key={a.assetId}
                  className="flex items-center justify-between gap-4 px-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{a.assetId}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {a.assetName || '—'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {renderSuggestion(a.assetId)}
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={isBusy(a.assetId)}
                      onClick={() => handleAdjust(a.assetId, 'MARK_MISSING')}
                    >
                      {t('reconcile.missingButton', {
                        defaultValue: 'Marcar como faltante',
                      })}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* HISTORY ------------------------------------------------------------- */}
        {adjustmentCount > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-2">
              <History className="h-4 w-4 text-muted-foreground" />
              <h4 className="font-semibold text-sm">
                {t('reconcile.historyHeading', { defaultValue: 'Historial de ajustes' })}
              </h4>
            </div>
            <ul className="divide-y rounded-md border text-xs">
              {history.data!.slice(0, 10).map((adj) => (
                <li key={adj.inventoryAdjustmentId} className="px-3 py-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <span className="font-medium">{adj.assetId}</span>
                      <span className="ml-2 text-muted-foreground">
                        {adj.action === 'ACCEPT_LOCATION'
                          ? t('reconcile.histAccept', { defaultValue: 'aceptó ubicación' })
                          : t('reconcile.histMissing', { defaultValue: 'marcado faltante' })}
                      </span>
                      {adj.userName && (
                        <span className="ml-2 text-muted-foreground">· {adj.userName}</span>
                      )}
                    </div>
                    <span className="text-muted-foreground whitespace-nowrap">
                      {format(new Date(adj.adjustmentDate), "d MMM yyyy HH:mm", { locale: es })}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </CardContent>
    </Card>
  );
}
